import { useCallback, useContext, useEffect, useRef, useState } from "react";
import Modal from "react-modal";
import IDaily_Route from "../../../models/IDaily_Route";
import IOrder from "../../../models/IOrder";
import UserContext from "../../../contexts/UserContext";
import SupplierContext from "../../../contexts/SupplierContext";
import { SocketContext } from "../../../contexts/SocketContext";
import UiContext from "../../../contexts/UiContext";
import INewDaily_Route from "../../../models/INewDaily_Route";
import OwTitle from "../../shared/OwTitle/OwTitle";
import OwLabel from "../../shared/OwLabel/OwLabel";
import OwButton from "../../shared/OwButton/OwButton";
import OwSelectOption from "../../shared/OwSelectOption/OwSelectOption";
import { useFormik } from "formik";
import OwDateInput from "../../shared/OwDateInput/OwDateInput";
import { Moment } from "moment-timezone";
import IProcessDailyRouteDTO from "../../../models/DTO/IProcessDailyRouteDTO";
import EndpointsEnum from "../../../models/EndpointsEnum";
import IOnProcessDailyRouteDTO from "../../../models/DTO/IOnProcessDailyRouteDTO";
import INotification from "../../../models/INotification";
import NotificationPositionEnum from "../../../models/NotificationPositionEnum";
import NotificationLevelEnum from "../../../models/NotificationLevelEnum";
import IUiSocketSetup from "../../../models/IUiSocketSetup";
import { buildRestUrlWithQuery, buildAuthHeadersForFetch } from "../../../services/RestService";
import RestEndpointsEnum from "../../../models/RestEndpointsEnum";
import IVehicle from "../../../models/IVehicle";
import IOnGetVehiclesDTO from "../../../models/DTO/IOnGetVehiclesDTO";
import IOnGetRouteTemplatesDTO, { IRouteTemplateResponse } from "../../../models/DTO/IOnGetRouteTemplatesDTO";
import IConnection_Token from "../../../models/IConnection_Token";
import IOnTypeaheadLookupByTokenTypeDTO from "../../../models/DTO/IOnTypeaheadLookupByTokenTypeDTO";
import TokenTypesEnum from "../../../models/TokenTypesEnum";
import IUser from "../../../models/IUser";
import { TypeaheadFn } from "../../shared/TypeaheadFn/TypeaheadFn";
import "./DailyRouteSidebar.scss";
import { useAppContextNonNull } from "@contexts/AppContext";
import OwPill from "@components/shared/OwPill/OwPill";
import DailyRouteService from "../../../services/DailyRouteService";
import { DailyRouteStatusWarningModal } from "../DailyRouteStatusWarningModal/DailyRouteStatusWarningModal";

type Props = {
    dailyRoute?: IDaily_Route;
    cancelFn: (refresh?: boolean) => void;
};

interface IOrderState {
    order: IOrder;
    status: "existing" | "added" | "removed";
}

export const DailyRouteSidebar = (props: Props) => {
    const { userModel, supplierModel, socket, uiContext } = useAppContextNonNull();

    const [dailyRoute] = useState<IDaily_Route | INewDaily_Route | undefined>(props.dailyRoute);
    const [isDisabled, setIsDisabled] = useState(true);
    const [vehicles, setVehicles] = useState<IVehicle[]>([]);
    const [availableUsers, setAvailableUsers] = useState<IConnection_Token[]>([]);
    const [selectedDriver, setSelectedDriver] = useState<IUser | undefined>(props.dailyRoute?.Driver ? props.dailyRoute.Driver : undefined);

    // Route selection state
    const [availableRoutes, setAvailableRoutes] = useState<IRouteTemplateResponse[]>([]);
    const [isLoadingRoutes, setIsLoadingRoutes] = useState<boolean>(true);
    const [selectedRouteId, setSelectedRouteId] = useState<number | undefined>(props.dailyRoute?.route_id);

    // Order management state
    const [orderStates, setOrderStates] = useState<IOrderState[]>(() => {
        // Initialize from existing daily route orders
        if (props.dailyRoute?.Daily_Route_Order_Maps) {
            return props.dailyRoute.Daily_Route_Order_Maps.filter((map) => map.Order).map((map) => ({
                order: map.Order!,
                status: "existing" as const,
            }));
        }
        return [];
    });
    const [searchResults, setSearchResults] = useState<IOrder[]>([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [isSearching, setIsSearching] = useState(false);
    const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const [showStatusWarningModal, setShowStatusWarningModal] = useState(false);
    const [ordersNeedingStatusChange, setOrdersNeedingStatusChange] = useState<IOrder[]>([]);
    const [pendingFormValues, setPendingFormValues] = useState<{
        route_date: string;
        route_id: number | undefined;
        vehicle_id: number | undefined;
    } | null>(null);

    const getVehicles = async () => {
        const url = buildRestUrlWithQuery({
            endpoint: RestEndpointsEnum.GetVehicles,
            query: { limit: 100, offset: 0 },
        });

        const headers = buildAuthHeadersForFetch({
            userModel,
            supplierModel,
        });

        try {
            const response = await fetch(url, { method: "GET", headers });
            const data: IOnGetVehiclesDTO = await response.json();
            setVehicles(data.vehicles);
        } catch (error) {
            console.error("Error fetching vehicles:", error);
        }
    };

    const getRoutes = async () => {
        try {
            const url = buildRestUrlWithQuery({
                endpoint: RestEndpointsEnum.GetRouteTemplates,
                query: {},
            });
            const headers = buildAuthHeadersForFetch({ userModel, supplierModel });
            const response = await fetch(url, { method: "GET", headers });
            const data: IOnGetRouteTemplatesDTO = await response.json();
            setAvailableRoutes(data.routes);
        } catch (error) {
            console.error("Error fetching routes:", error);
        } finally {
            setIsLoadingRoutes(false);
        }
    };

    const searchUsers = async (name: string) => {
        if (!name.trim()) {
            setAvailableUsers([]);
            return;
        }

        const url = buildRestUrlWithQuery({
            endpoint: RestEndpointsEnum.TokensSearch,
            query: {
                tokenType: TokenTypesEnum.SUPPLIER_USER,
                name,
                limit: 10,
            },
        });

        const headers = buildAuthHeadersForFetch({
            userModel,
            supplierModel,
        });

        try {
            const response = await fetch(url, { method: "GET", headers });
            if (response.ok) {
                const data = (await response.json()) as IOnTypeaheadLookupByTokenTypeDTO;
                setAvailableUsers(data.results || []);
            }
        } catch (error) {
            console.error("Error searching users:", error);
            setAvailableUsers([]);
        }
    };

    const handleDriverSelected = (user: IUser) => {
        setSelectedDriver(user);
        setAvailableUsers([]);
    };

    const clearDriver = () => {
        setSelectedDriver(undefined);
    };

    const renderAvailableUser = (user: IUser) => {
        return (
            <div>
                <div>{user?.name}</div>
                {user?.email && <small className="text-muted">{user.email}</small>}
            </div>
        );
    };

    // Search orders via REST API with debounce
    const searchOrders = useCallback(
        async (query: string) => {
            if (!query.trim()) {
                setSearchResults([]);
                setIsSearching(false);
                return;
            }

            setIsSearching(true);

            try {
                const url = buildRestUrlWithQuery({
                    endpoint: RestEndpointsEnum.GetOrdersWithAuth,
                    query: {
                        queryString: `order=${query}`,
                        limit: 10,
                    },
                });

                const headers = buildAuthHeadersForFetch({
                    userModel,
                    supplierModel,
                });

                const response = await fetch(url, { method: "GET", headers });

                if (response.ok) {
                    const data = await response.json();

                    // Filter out orders already in the list (not removed)
                    const existingOrderIds = orderStates.filter((os) => os.status !== "removed").map((os) => os.order.id);

                    setSearchResults(data.orders.filter((o: IOrder) => !existingOrderIds.includes(o.id)));
                }
            } catch (error) {
                console.error("Error searching orders:", error);
                setSearchResults([]);
            } finally {
                setIsSearching(false);
            }
        },
        [userModel, supplierModel, orderStates]
    );

    // Debounced search handler
    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const query = e.target.value;
        setSearchQuery(query);

        // Clear existing timeout
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        // Set new timeout for debounce
        searchTimeoutRef.current = setTimeout(() => {
            searchOrders(query);
        }, 300);
    };

    // Add order to the list
    const addOrder = (order: IOrder) => {
        setOrderStates((prev) => [...prev, { order, status: "added" }]);
        setSearchResults([]);
        setSearchQuery("");
    };

    // Remove order from the list
    const removeOrder = (orderId: number) => {
        setOrderStates(
            (prev) =>
                prev
                    .map((os) => {
                        if (os.order.id !== orderId) return os;

                        // If it was just added, remove it entirely
                        if (os.status === "added") {
                            return null;
                        }

                        // If existing, mark as removed (strikethrough)
                        return { ...os, status: "removed" as const };
                    })
                    .filter(Boolean) as IOrderState[]
        );
    };

    // Restore a removed order
    const restoreOrder = (orderId: number) => {
        setOrderStates((prev) =>
            prev.map((os) => {
                if (os.order.id !== orderId) return os;
                return { ...os, status: "existing" as const };
            })
        );
    };

    const cancel = () => {
        props.cancelFn();
    };

    // Route selection helpers
    const canChangeRoute = (): boolean => {
        if (!props.dailyRoute) return true;
        const status = props.dailyRoute.status;
        return !status || status === "not_started";
    };

    const getCurrentRouteName = (): string => {
        if (props.dailyRoute?.Route?.name) return props.dailyRoute.Route.name;
        const route = availableRoutes.find((r) => r.id === selectedRouteId);
        return route?.name || "";
    };

    const handleRouteChange = (routeId: number) => {
        const selectedRoute = availableRoutes.find((r) => r.id === routeId);
        if (!selectedRoute) return;

        setSelectedRouteId(routeId);

        // Auto-populate driver if route has default
        if (selectedRoute.default_driver) {
            setSelectedDriver({
                id: selectedRoute.default_driver.id,
                guid: selectedRoute.default_driver.guid,
                name: selectedRoute.default_driver.name,
            } as IUser);
        }

        // Auto-populate vehicle if route has default
        if (selectedRoute.default_vehicle) {
            formik.setFieldValue("vehicle_id", selectedRoute.default_vehicle.id);
        }
    };

    const processDailyRoute = (values: { route_date: string; route_id: number | undefined; vehicle_id: number | undefined }) => {
        // Get order IDs to add and remove
        const addedOrderIds = orderStates.filter((os) => os.status === "added").map((os) => os.order.id);
        const removedOrderIds = orderStates.filter((os) => os.status === "removed").map((os) => os.order.id);

        // Build the daily route object - either from existing or create new
        const dailyRouteData = dailyRoute
            ? {
                  ...dailyRoute,
                  route_id: selectedRouteId!,
                  route_date: values.route_date,
                  vehicle_id: values.vehicle_id ?? null,
                  driver_user_id: selectedDriver?.id ?? null,
              }
            : {
                  route_id: selectedRouteId!,
                  route_date: values.route_date,
                  vehicle_id: values.vehicle_id ?? null,
                  driver_user_id: selectedDriver?.id ?? null,
              };

        const payload: IProcessDailyRouteDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            dailyRoutes: [
                {
                    dailyRoute: dailyRouteData,
                    orders: addedOrderIds.length > 0 ? addedOrderIds : undefined,
                    removeOrders: removedOrderIds.length > 0 ? removedOrderIds : undefined,
                },
            ],
        };

        socket.emit(EndpointsEnum.RoutesProcessDailyRoute, payload);
    };

    const onProcessDailyRoute = (_data: IOnProcessDailyRouteDTO) => {
        const notification: INotification = {
            position: NotificationPositionEnum.bc,
            level: NotificationLevelEnum.success,
            message: "successfully_processed_daily_route",
        };

        uiContext.notifyFn(null, notification);

        props.cancelFn(true);
    };

    const sockets: IUiSocketSetup[] = [{ endpoint: EndpointsEnum.RoutesOnProcessDailyRoute, fn: onProcessDailyRoute }];

    useEffect(() => {
        for (const _socket of sockets) {
            socket.on(_socket.endpoint, _socket.fn);
        }

        getVehicles();
        getRoutes();

        return () => {
            for (const _socket of sockets) {
                socket.removeListener(_socket.endpoint, _socket.fn);
            }
            // Clear search timeout on unmount
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSubmit = (values: { route_date: string; route_id: number | undefined; vehicle_id: number | undefined }) => {
        // Get all active orders (added + existing, not removed)
        const activeOrdersList = orderStates.filter((os) => os.status !== "removed").map((os) => os.order);

        // Validate at least one order is assigned
        if (activeOrdersList.length === 0) {
            const notification: INotification = {
                message: "at_least_one_order_required",
                position: NotificationPositionEnum.bc,
                level: NotificationLevelEnum.warning,
            };
            uiContext.notifyFn(null, notification);
            return;
        }

        // Check for orders needing status change (not in READY_FOR_DELIVERY or READY_FOR_PICKUP)
        const needsStatusChange = DailyRouteService.getOrdersNeedingStatusChange(activeOrdersList);

        if (needsStatusChange.length > 0) {
            setOrdersNeedingStatusChange(needsStatusChange);
            setPendingFormValues(values);
            setShowStatusWarningModal(true);
        } else {
            processDailyRoute(values);
        }
    };

    const confirmProceed = () => {
        setShowStatusWarningModal(false);
        if (pendingFormValues) {
            processDailyRoute(pendingFormValues);
        }
        setPendingFormValues(null);
        setOrdersNeedingStatusChange([]);
    };

    const cancelStatusWarning = () => {
        setShowStatusWarningModal(false);
        setPendingFormValues(null);
        setOrdersNeedingStatusChange([]);
    };

    const formik = useFormik({
        initialValues: {
            route_id: dailyRoute?.route_id || undefined,
            route_date: dailyRoute?.route_date || "",
            vehicle_id: dailyRoute?.vehicle_id || undefined,
        },
        onSubmit: (values) => {
            handleSubmit(values);
        },
        validate: (values) => {
            const errors: any = {};

            if (!values.route_date) {
                errors.route_date = "Required";
            }

            if (!selectedRouteId) {
                errors.route_id = "Required";
            }

            // Require at least one active order
            const activeOrderCount = orderStates.filter((os) => os.status !== "removed").length;
            if (activeOrderCount === 0) {
                errors.orders = "at_least_one_order_required";
            }

            setIsDisabled(Object.keys(errors).length > 0);

            return errors;
        },
    });

    // Run validation when selectedRouteId changes to update save button state
    useEffect(() => {
        formik.validateForm();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedRouteId]);

    // Run validation when orderStates changes to update save button state
    useEffect(() => {
        formik.validateForm();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orderStates]);

    // Count orders by status for display
    const activeOrders = orderStates.filter((os) => os.status !== "removed");
    const removedOrders = orderStates.filter((os) => os.status === "removed");

    return (
        <div className={"DailyRouteSidebar p-2"}>
            <div className={"row"}>
                <div className={"col-12"}>
                    <div className="text-end">
                        <button className="btn btn-danger btn-sm" onClick={cancel}>
                            <i className="far fa-times" />
                        </button>
                    </div>
                    <OwTitle className={"mb-2"} leftIconName={"fad fa-map-marked-alt"} title={"daily_route"} />
                    <hr />
                </div>
            </div>

            <div>{!canChangeRoute() && <OwPill title="read_only_daily_route" className="bg-danger text-white mb-2" />}</div>

            <div className="row">
                <form onSubmit={formik.handleSubmit}>
                    {/* Route Selection */}
                    <div className={"d-flex flex-column mb-4"}>
                        <div>
                            <OwLabel title={"route"} isBold={true} />
                        </div>
                        <div>
                            {isLoadingRoutes ? (
                                <div className="text-center p-2">
                                    <i className="fas fa-spinner fa-spin text-muted" />
                                </div>
                            ) : canChangeRoute() ? (
                                <select
                                    className="form-select form-select-sm"
                                    value={selectedRouteId || ""}
                                    onChange={(e) => handleRouteChange(Number(e.target.value))}>
                                    <option value="" disabled>
                                        Select route...
                                    </option>
                                    {availableRoutes.map((route) => (
                                        <option key={route.id} value={route.id}>
                                            {route.name}
                                        </option>
                                    ))}
                                </select>
                            ) : (
                                <div className="">{getCurrentRouteName()}</div>
                            )}
                        </div>
                    </div>

                    <div className={"d-flex flex-column mb-4"}>
                        <div>
                            <OwLabel title={"route_date"} isBold={true} />
                        </div>
                        <div className={""}>
                            <OwDateInput
                                fn={(date: Moment) => {
                                    const e = {
                                        target: {
                                            value: date.format("YYYY-MM-DD"),
                                            name: "route_date",
                                        },
                                    };

                                    formik.handleChange(e);
                                }}
                                selectedDate={formik.values.route_date}
                                disabled={!canChangeRoute()}
                            />
                        </div>
                    </div>

                    <div className={"d-flex flex-column mb-4"}>
                        <div>
                            <OwLabel title={"vehicle"} isBold={true} />
                        </div>
                        <div>
                            <OwSelectOption
                                name="vehicle_id"
                                options={vehicles}
                                selectedOption={{ vehicle_id: formik.values.vehicle_id }}
                                selectedOptionProp="vehicle_id"
                                valueProp="id"
                                titleProp="name"
                                fn={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                    const value = e.target.value;
                                    formik.setFieldValue("vehicle_id", value ? Number(value) : undefined);
                                }}
                                showBlankFirst={true}
                                isDisabled={!canChangeRoute()}
                            />
                        </div>
                    </div>

                    <div className={"d-flex flex-column mb-4"}>
                        <div>
                            <OwLabel title={"driver"} isBold={true} />
                        </div>
                        <div>
                            <TypeaheadFn
                                lookupFn={searchUsers}
                                availableOptions={availableUsers.map((token) => token.User).filter(Boolean) as IUser[]}
                                selectedOption={selectedDriver}
                                valueProp="id"
                                selectedOptionFn={handleDriverSelected}
                                emitSelectionSetup={{ emitObject: true }}
                                titleProp="name"
                                subTitleProp="email"
                                renderFn={renderAvailableUser}
                                placeholder="search_for_driver"
                                clearSelectionFn={clearDriver}
                                disabled={!canChangeRoute()}
                            />
                        </div>
                    </div>

                    {/* Order Search */}
                    <div className={"d-flex flex-column mb-3"}>
                        <div>
                            <OwLabel title={"add_order"} isBold={true} />
                        </div>
                        <div className="position-relative">
                            <input
                                type="text"
                                className="form-control form-control-sm"
                                placeholder="Search by order number..."
                                value={searchQuery}
                                onChange={handleSearchChange}
                                disabled={!canChangeRoute()}
                            />
                            {isSearching && (
                                <div className="position-absolute end-0 top-50 translate-middle-y me-2">
                                    <i className="fas fa-spinner fa-spin text-muted" />
                                </div>
                            )}
                            {/* Search Results Dropdown */}
                            {searchResults.length > 0 && (
                                <div className="search-results-dropdown">
                                    {searchResults.map((order) => {
                                        const validation = DailyRouteService.validateOrderForDailyRoute(order);
                                        return (
                                            <div
                                                key={order.guid}
                                                className={`search-result-item ${validation.isBlocked ? "disabled" : ""}`}
                                                onClick={() => !validation.isBlocked && addOrder(order)}
                                                title={validation.isBlocked ? validation.blockReason : undefined}>
                                                <span>{order.name}</span>
                                                {order.Vendor && <small className="text-muted ms-2">({order.Vendor.name})</small>}
                                                {validation.isBlocked && (
                                                    <span className="ms-2 text-danger">
                                                        <i className="fas fa-ban me-1" />
                                                        <OwLabel title={validation.blockReason!} />
                                                    </span>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Orders on Route */}
                    <div className={"d-flex flex-column mb-4"}>
                        <div>
                            <OwLabel title={"orders_on_route"} isBold={true} />
                        </div>
                        <div className="order-list">
                            {orderStates.length === 0 ? (
                                <div className="text-muted small p-2 text-center">
                                    <OwLabel title={"no_orders_on_route"} />
                                </div>
                            ) : (
                                <>
                                    {/* Active orders (existing + added) first */}
                                    {activeOrders.map(({ order, status }) => (
                                        <div
                                            key={order.guid}
                                            className={`order-item d-flex align-items-center justify-content-between ${status}`}>
                                            <span>
                                                {status === "added" && <i className="fas fa-plus-circle text-success me-2" />}
                                                {order.name}
                                            </span>
                                            {canChangeRoute() && (
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-link text-danger p-0 pointer"
                                                    onClick={() => removeOrder(order.id)}>
                                                    <i className="fas fa-times" />
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                    {/* Removed orders at the bottom */}
                                    {removedOrders.map(({ order }) => (
                                        <div
                                            key={order.guid}
                                            className="order-item d-flex align-items-center justify-content-between removed">
                                            <span className="text-decoration-line-through text-muted">{order.name}</span>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-link p-0"
                                                onClick={() => restoreOrder(order.id)}>
                                                <i className="fas fa-undo" />
                                            </button>
                                        </div>
                                    ))}
                                </>
                            )}
                        </div>
                        {/* Warning when no active orders */}
                        {activeOrders.length === 0 && canChangeRoute() && (
                            <div className="text-danger small mt-1">
                                <i className="fas fa-exclamation-circle me-1" />
                                <OwLabel title="at_least_one_order_required" />
                            </div>
                        )}
                    </div>

                    <div>
                        {canChangeRoute() && <OwButton type={"submit"} title={"save"} disableOnActive={true} disabled={isDisabled} />}
                        {!canChangeRoute() && (
                            <OwButton type={"button"} className="btn-danger" title={"close"} disableOnActive={true} fn={cancel} />
                        )}
                    </div>
                </form>
            </div>

            {/* Status Warning Modal */}
            <Modal
                isOpen={showStatusWarningModal}
                contentLabel="Order Status Warning"
                shouldCloseOnOverlayClick={false}
                className="modalContentClass d-flex justify-content-center"
                overlayClassName="modalOverlayClass">
                <DailyRouteStatusWarningModal
                    ordersNeedingStatusChange={ordersNeedingStatusChange}
                    proceedFn={confirmProceed}
                    cancelFn={cancelStatusWarning}
                />
            </Modal>
        </div>
    );
};
